import { useState } from 'react'
import { useRevogarOs } from '../../../api/queries/ordensDeServico'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro } from '../../../components/Estados'
import { dataHora } from '../../../lib/datas'
import type { DetalheDaOs, RevogacaoDaOs } from '../../../types/ordemDeServico'
import { DialogoDeTexto } from '../../chamados/fiscalizacao/DialogoDeTexto'
import { OS } from '../textosDaOs'

/**
 * O que se pode fazer com uma ordem já registrada: revogá-la. Só isso.
 *
 * **Não existe editar, e a ausência é a funcionalidade.** A ordem autoriza despesa e é o marco de
 * que o contrato conta prazo — corrigi-la por cima apagaria qual texto autorizou o quê. No sistema
 * anterior, 16 ordens foram apagadas fisicamente e 83 linhas de valor foram junto, sem trilha
 * nenhuma. Aqui a retirada fica registrada com autor, data e motivo, e a ordem continua na lista.
 *
 * O botão some para quem não é da fiscalização e para a ordem já revogada. Isso é conveniência de
 * tela: quem chamar o endpoint na mão continua sendo recusado pelo servidor (`@PreAuthorize(FISCAL)`
 * e `OS_JA_REVOGADA`).
 *
 * @param props a ordem carregada e se quem está vendo pode escrever
 * @returns o botão de revogar, ou nada
 */
export function AcoesDaOs({ ordem, podeEscrever }: { ordem: DetalheDaOs; podeEscrever: boolean }) {
  const [revogando, definirRevogando] = useState(false)
  const revogar = useRevogarOs()

  if (!podeEscrever || ordem.revogacao) {
    return null
  }

  return (
    <>
      <Botao aparencia="perigo" onClick={() => definirRevogando(true)}>
        {OS.revogar}
      </Botao>

      {revogar.isError && (
        <div className="w-full">
          <AvisoDeErro erro={revogar.error} />
        </div>
      )}

      {revogando && (
        <DialogoDeTexto
          titulo={OS.revogar}
          ajuda={`${OS.naoSeEdita.ajuda} ${OS.revogarAjuda}`}
          rotulo="Por que esta ordem está sendo revogada"
          aoConfirmar={(motivo) => {
            revogar.mutate({ id: ordem.id, motivo })
            definirRevogando(false)
          }}
          aoFechar={() => definirRevogando(false)}
        />
      )}
    </>
  )
}

/**
 * A ordem revogada, dita por inteiro.
 *
 * Ela continua visível de propósito: foi ela que autorizou despesa antes de ser retirada, e sumir
 * da tela faria a trilha do contrato ter um buraco exatamente no lugar em que alguém corrigiu algo.
 *
 * @param props a retirada registrada
 * @returns a faixa da revogação
 */
export function AvisoDeRevogacao({ revogacao }: { revogacao: RevogacaoDaOs }) {
  return (
    <section aria-label={OS.revogar} className="rounded-xl border border-slate-300 bg-slate-100 p-4">
      <h2 className="text-sm font-semibold text-slate-900">Esta ordem de serviço foi revogada</h2>
      <p className="mt-1 text-sm text-slate-700">{revogacao.motivo}</p>
      <p className="mt-1 text-xs text-slate-600">{OS.revogadaPor(revogacao.porNome, dataHora(revogacao.em))}</p>
      <p className="mt-2 max-w-prose text-xs text-slate-600">{OS.revogarAjuda}</p>
    </section>
  )
}
